// src/auth/components/RegisterComponent.jsx

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../authContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DataProtectionInformation from '../legal/DataProtectionInformation';
import { consumePostAuthRedirect } from '../postAuthRedirect';
import TermsAcceptanceControl from '../legal/TermsAcceptanceControl';
import { getTermsMetadata } from '../legal/termsAcceptanceApi';

const RegisterComponent = ({ onSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const auth = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    acceptPrivacyPolicy: false,
    receiveInformation: false,
    acceptTerms: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsMetadata, setTermsMetadata] = useState(null);
  const [termsState, setTermsState] = useState('loading');
  const [termsError, setTermsError] = useState('');
  const [registrationError, setRegistrationError] = useState('');

  const loadTermsMetadata = useCallback(async () => {
    setTermsState('loading');
    try {
      setTermsMetadata(await getTermsMetadata(apiUrl));
      setTermsState('ready');
      setTermsError('');
    } catch (_error) {
      setTermsState('error');
      setTermsError('terms_metadata_unavailable');
    }
  }, [apiUrl]);

  useEffect(() => { loadTermsMetadata(); }, [loadTermsMetadata]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setRegistrationError('');

    if (
      !formData.email ||
      !formData.password ||
      !formData.acceptPrivacyPolicy ||
      !formData.acceptTerms ||
      !termsMetadata
    ) {
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await axios.post(`${apiUrl}/users/register`, {
        ...formData,
        termsDocumentVersion: termsMetadata.publishedDocument.version,
        termsRequirementId: termsMetadata.acceptanceRequirement.id,
        email: formData.email.trim().toLowerCase()
      });

      const { token } = response.data;
      localStorage.setItem('token', token);
      await auth.fetchUserData(token);

      if (onSuccess) onSuccess();
      navigate(consumePostAuthRedirect(location.state), { replace: true });
    } catch (error) {
      if (error.response?.data?.error === 'TERMS_ACCEPTANCE_STALE') {
        setFormData(current => ({ ...current, acceptTerms: false }));
        setTermsError('terms_acceptance_stale');
        await loadTermsMetadata();
      } else if (error.response?.data === 'Email already exists') {
        alert(t('email_already_exists'));
      } else if (error.response?.data?.error === 'DEMO_REGISTRATION_NOT_ADMITTED') {
        setRegistrationError(t(
          'demo_registration_not_admitted',
          'Registration is restricted to invited Demo participants.'
        ));
      } else {
        console.error('Error registering:', error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="web-modal-form">

      <input
        type="email"
        name="email"
        placeholder={t('email')}
        value={formData.email}
        onChange={handleChange}
        className="web-modal-input"
        required
      />

      <input
        type="password"
        name="password"
        placeholder={t('password')}
        value={formData.password}
        onChange={handleChange}
        className="web-modal-input"
        required
      />

      <div className="web-modal-checkbox-group" data-testid="registration-agreements">
        <DataProtectionInformation
          formData={formData}
          handleChange={handleChange}
          fontSize="0.8em"
        />

        <TermsAcceptanceControl
          checked={formData.acceptTerms}
          disabled={termsState !== 'ready'}
          error={termsError}
          onChange={handleChange}
        />
      </div>

      {termsState === 'error' && (
        <button type="button" onClick={loadTermsMetadata}>
          {t('retry', 'Retry')}
        </button>
      )}

      {registrationError && <p role="alert">{registrationError}</p>}

      <button type="submit" className="web-modal-button" disabled={isSubmitting || termsState !== 'ready'}>
        {isSubmitting ? t('registering', 'Registering...') : t('register')}
      </button>
    </form>
  );
};

export default RegisterComponent;
